import { useQuery } from '@tanstack/react-query'
import { GitCompare } from 'lucide-react'
import { useState } from 'react'

import { pedir } from '../../api/nucleo'
import { Boton, Modal, Pastilla, Tarjeta } from '../../componentes/ui'

/**
 * Los componentes que tu web tiene personalizados (CLAUDE.md §11): lo demás lo hereda de la base y recibe sus mejoras
 * solo. Uno **atrasado** es uno tuyo cuya versión en la base ha cambiado desde que lo personalizaste: no se toca nada
 * solo; aquí se ve lo tuyo al lado de lo de la base, para decidir. Lo calcula el núcleo
 * (`/versiones-de-tema/componentes`); lo ve quien ve el diseño.
 */
type Componente = { ruta: string; estado: 'personalizado' | 'atrasado' | 'propio'; desde: null | number; versionBase: null | string }

const ETIQUETA: Record<Componente['estado'], { texto: string; clave: 'neutra' | 'sin-publicar' | 'violeta' }> = {
  personalizado: { texto: 'Personalizado', clave: 'violeta' },
  atrasado: { texto: 'La base ha cambiado', clave: 'sin-publicar' },
  propio: { texto: 'Solo tuyo', clave: 'neutra' },
}

const Comparar = ({ sitio, ruta, cerrar }: { sitio: number; ruta: string; cerrar: () => void }) => {
  const { data, isPending, error } = useQuery({
    queryKey: ['componente-comparar', sitio, ruta],
    queryFn: () => pedir<{ deLaWeb: string; deLaBase: null | string }>(`/versiones-de-tema/componentes/comparar?sitio=${sitio}&ruta=${encodeURIComponent(ruta)}`),
  })
  return (
    <Modal ancho="max-w-6xl" cerrar={cerrar} pie={<Boton onClick={cerrar}>Cerrar</Boton>} titulo={`${ruta}: tu versión y la de la base`}>
      <p className="mb-3 text-sm text-tenue">
        A la izquierda, el de tu web; a la derecha, el que tiene hoy la base. Si quieres las mejoras de la base sin perder lo tuyo, pídeselo a Diseño con IA o escríbenos: nada se cambia solo.
      </p>
      {isPending ? (
        <p className="text-sm text-tenue">Cargando…</p>
      ) : error || !data ? (
        <p className="text-sm text-error">No se ha podido leer el fichero.</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {[
            ['Tu web', data.deLaWeb],
            ['La base hoy', data.deLaBase ?? '(la base ya no tiene este fichero)'],
          ].map(([titulo, texto]) => (
            <div className="min-w-0" key={titulo}>
              <p className="mb-1 text-[13px] font-medium">{titulo}</p>
              <pre className="max-h-[60vh] overflow-auto rounded-campo border border-linea bg-papel-2 p-3 text-xs leading-relaxed">{texto}</pre>
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}

export const ComponentesDelDiseno = ({ sitio }: { sitio: number }) => {
  const { data } = useQuery({ queryKey: ['componentes-del-diseno', sitio], queryFn: () => pedir<{ componentes: Componente[] }>(`/versiones-de-tema/componentes?sitio=${sitio}`), retry: false, staleTime: 60_000 })
  const [comparando, setComparando] = useState<null | string>(null)
  const componentes = data?.componentes ?? []
  if (componentes.length === 0) return null
  const atrasados = componentes.filter((c) => c.estado === 'atrasado').length
  return (
    <Tarjeta>
      <h2 className="font-display text-base font-semibold">Componentes personalizados</h2>
      <p className="mt-1 text-[13px] text-tenue">
        Lo que tu web tiene propio. Todo lo demás sale de la base y recibe sus mejoras solo.
        {atrasados > 0 ? ` En ${atrasados === 1 ? 'uno' : atrasados} la base ha cambiado desde que lo personalizaste: compáralos para decidir.` : ''}
      </p>
      <ul className="mt-3 flex flex-col divide-y divide-linea">
        {componentes.map((c) => (
          <li className="flex flex-wrap items-center justify-between gap-2 py-2" key={c.ruta}>
            <div className="min-w-0">
              <code className="text-[13px]">{c.ruta}</code>
              {c.desde && <span className="ml-2 text-xs text-tenue">desde la v{c.desde}</span>}
            </div>
            <span className="flex items-center gap-2">
              <Pastilla clave={ETIQUETA[c.estado].clave}>{ETIQUETA[c.estado].texto}</Pastilla>
              {c.estado !== 'propio' && (
                <Boton icono={<GitCompare />} onClick={() => setComparando(c.ruta)} pequeno tipo="discreto">
                  Comparar con la base
                </Boton>
              )}
            </span>
          </li>
        ))}
      </ul>
      {comparando && <Comparar cerrar={() => setComparando(null)} ruta={comparando} sitio={sitio} />}
    </Tarjeta>
  )
}
